// src/hooks/useScrollToTop.ts
import { useEffect, useRef, useCallback } from 'react';
import logger from '../utils/logger';

interface UseScrollToTopOptions {
  threshold?: number;
  onScrollToTop: () => void;
  isLoading?: boolean;
  hasMore?: boolean;
  externalRef?: React.RefObject<HTMLDivElement>;
}

export const useScrollToTop = ({
  threshold = 100,
  onScrollToTop,
  isLoading = false,
  hasMore = true,
  externalRef
}: UseScrollToTopOptions) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const scrollElementRef = externalRef || internalRef;
  const isLoadingRef = useRef(isLoading);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const handleScroll = useCallback(() => {
    const element = scrollElementRef.current;
    if (!element) return;
    
    const scrollTop = element.scrollTop;
    const isNearTop = scrollTop <= threshold;
    
    if (isNearTop && !isLoadingRef.current && hasMore) {
      logger.log('📍 Scroll detected: Loading older messages');
      onScrollToTop();
    }
  }, [threshold, onScrollToTop, hasMore]);

  useEffect(() => {
    const element = scrollElementRef.current;
    if (!element) return;

    element.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      element.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return externalRef ? internalRef : scrollElementRef;
};